import React, { useState, useContext, useRef, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { SocketContext } from "../context/SocketProvider";
import { GAME_STAGES, DEFAULT_ROOM } from "../constants/gameStages";
import { FaChevronRight, FaChevronDown } from "react-icons/fa6";
import { useRoleBackground } from "../hooks/useRoleBackground";
import mascot from "../assets/AICOMちゃん.svg";

/**
 * HomePage component - Main game lobby shown after role assignment.
 * Contains: role swap toggle, language selector, Start button, Data Collection toggle.
 */
const HomePage = ({
  gameStage,
  role,
  socket,
  setGameStarted,
  setWarning,
  warning,
  navigate,
  canSwapRoles,
}) => {
  const { t, language, setLanguage } = useLanguage();
  const { playMode, setPlayMode, roomFull } = useContext(SocketContext);
  const [langOpen, setLangOpen] = useState(false);
  const [infoPopupOpen, setInfoPopupOpen] = useState(false);
  const langRef = useRef(null);

  // Data Collection ON ↔ game mode, OFF ↔ private mode
  const dataCollectionOn = playMode === "game";

  // Close language dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target))
        setLangOpen(false);
    };
    if (langOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [langOpen]);

  const handleStartGame = () => {
    if (gameStage === GAME_STAGES.WAITING) {
      setWarning(
        t("warning.waitingPlayers", "Waiting For Both Players To Connect"),
      );
      setTimeout(() => setWarning(null), 3000);
    } else if (gameStage !== GAME_STAGES.PAUSE) {
      // If Data Collection is ON, show consent/ID page first; Next there will enter the game
      if (playMode === "game") {
        navigate("/datacollection");
        return;
      }
      // Private mode: go straight into the game
      // Set default gamemode to "normal" and skip game mode selection pages, but show the how to play page
      const defaultMode = "normal";
      socket.emit("game_started", { room: DEFAULT_ROOM });
      socket.emit("gamemode_selected", {
        mode: defaultMode,
        room: DEFAULT_ROOM,
      });
      socket.emit("gamemode_confirmed", {
        mode: defaultMode,
        room: DEFAULT_ROOM,
      });
      setGameStarted(true);
      // Skip /gamemode and /waitingforgamemode, go to showgamemode (how to play)
      navigate("/showgamemode", { state: { mode: defaultMode } });
    }
  };

  const handleSwapRoles = () => {
    socket.emit("swap_roles", { room: DEFAULT_ROOM });
  };

  const handleDataCollectionToggle = (on) => {
    const mode = on ? "game" : "private";
    setPlayMode(mode);
    socket.emit("select_play_mode", {
      room: DEFAULT_ROOM,
      playMode: mode,
      reset: false,
    });
  };

  // Color theme follows role
  const isDescriber = role === "describer";
  const bgDark = isDescriber ? "bg-pink400" : "bg-blue400";
  const bgLight = isDescriber ? "bg-pink150" : "bg-blue150";
  const textAccent = isDescriber ? "text-pink200" : "text-blue200";
  const borderAccent = isDescriber ? "border-pink400" : "border-blue400";

  // Get role-specific background
  const { bgStyle, className: bgClassName } = useRoleBackground(
    isDescriber ? "describer" : "guesser",
  );

  return (
    <div
      className={`h-screen flex flex-col items-center justify-center text-black px-4 text-center relative ${bgClassName}`}
      style={bgStyle}
    >
      {/* Role Swap Toggle — Top Left */}
      <div className="absolute top-6 left-6 z-10">
        <div className={`relative flex ${!canSwapRoles ? "opacity-50" : ""}`}>
          <div className={`absolute inset-0 ${bgLight} rounded-lg`}></div>
          <div
            className={`absolute inset-0 ${bgDark} rounded-lg transition-transform duration-200 ${
              isDescriber ? "translate-x-0" : "translate-x-32"
            }`}
            style={{ width: "128px" }}
          ></div>
          <button
            onClick={canSwapRoles ? handleSwapRoles : undefined}
            disabled={!canSwapRoles}
            className={`relative z-10 w-32 px-4 py-2 font-bold text-xl transition-colors duration-200 ${
              isDescriber ? "text-white" : textAccent
            } ${!canSwapRoles ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            {t("role.describer", "Describer")}
          </button>
          <button
            onClick={canSwapRoles ? handleSwapRoles : undefined}
            disabled={!canSwapRoles}
            className={`relative z-10 w-32 px-4 py-2 font-bold text-xl transition-colors duration-200 ${
              !isDescriber ? "text-white" : textAccent
            } ${!canSwapRoles ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            {t("role.guesser", "Guesser")}
          </button>
        </div>
      </div>

      {/* Language Dropdown — Top Right */}
      <div className="absolute top-3 right-6 z-10" ref={langRef}>
        <button
          onClick={() => setLangOpen(!langOpen)}
          className={`toggle-container ${borderAccent} flex`}
        >
          <span
            className={`toggle-btn ${bgDark} text-white flex items-center gap-3`}
            style={{
              width: "84px",
              height: "45px",
            }}
          >
            {language === "en" ? "EN" : "JP"}
            <FaChevronDown
              className={`transition-transform ${langOpen ? "rotate-180" : ""}`}
              style={{ fontSize: "20px", minWidth: "20px" }}
            />
          </span>
        </button>
        {langOpen && (
          <div
            className={`absolute top-full right-0 mt-1 w-[80px] bg-white border-[3px] ${borderAccent} rounded-md shadow-lg overflow-hidden`}
          >
            {["en", "ja"].map((lang) => (
              <button
                key={lang}
                onClick={() => {
                  setLanguage(lang);
                  setLangOpen(false);
                }}
                className={`w-full px-1 py-2 text-center font-bold font-['Gabarito'] transition-all hover:shadow-[inset_0_4px_0_0_rgba(0,0,0,0.25)] ${
                  language === lang
                    ? `${bgDark} text-white`
                    : `${textAccent} bg-white`
                }`}
              >
                {lang === "en" ? "ENGLISH" : "日本語"}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Title */}
      <h1 className="text-8xl font-['Gabarito'] font-bold leading-tight mb-8 whitespace-nowrap">
        {t("home.title", "MIND THE AI GAP")}
      </h1>

      {/* Mascot */}
      <img
        src={mascot}
        alt="Mascot"
        className="w-[168px] h-[312px] mb-[-56px]"
      />

      {/* Start Button */}
      <button
        className={`w-[288px] h-[90px] bg-yellow-300 hover:bg-yellow 300 rounded-[20px] shadow transition duration-300 mb-6 relative ${roomFull ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={handleStartGame}
        disabled={roomFull}
      >
        <span className="btn-text-pos-right-xl text-black font-semibold text-4xl">
          {t("button.start", "Start!")}
        </span>
        <FaChevronRight className="arrow-pos-right-xl text-4xl text-black" />
      </button>

      {/* Warning */}
      {warning && <p className="text-red-500 mb-4">{warning}</p>}
      {roomFull && (
        <p className="text-red-500 mb-4">
          {t("warning.roomFull", "This room is full. Please use a different room.")}
        </p>
      )}

      {/* Waiting hint */}
      {gameStage === GAME_STAGES.WAITING && (
        <p className="text-lg font-semibold opacity-70 mb-4">
          {t("warning.waitingPlayers", "Waiting For Both Players To Connect")}
        </p>
      )}

      {/* Data Collection Toggle */}
      <div className="flex items-center gap-3 mt-2">
        <span className="text-lg font-bold tracking-wide">
          {t("home.dataCollection", "DATA COLLECTION:")}
        </span>
        <div className={`relative flex`}>
          <div className={`absolute inset-0 ${bgLight} rounded-lg`}></div>
          <div
            className={`absolute inset-0 ${bgDark} rounded-lg transition-transform duration-200 ${
              dataCollectionOn ? "translate-x-0" : "translate-x-20"
            }`}
            style={{ width: "80px" }}
          ></div>
          <button
            onClick={() => handleDataCollectionToggle(true)}
            className={`relative z-10 w-20 px-4 py-2 font-bold text-xl transition-colors duration-200 ${
              dataCollectionOn ? "text-white" : textAccent
            } cursor-pointer`}
          >
            {t("home.on", "ON")}
          </button>
          <button
            onClick={() => handleDataCollectionToggle(false)}
            className={`relative z-10 w-20 px-4 py-2 font-bold text-xl transition-colors duration-200 ${
              !dataCollectionOn ? "text-white" : textAccent
            } cursor-pointer`}
          >
            {t("home.off", "OFF")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
